import React, { useState, useEffect } from "react";
import "./App.css";
import Swal from "sweetalert2";
import ProductMenu from "./components/ProductMenu";
import { login } from "./components/authService";
import {
  getSessionData,
  getCartSessionData,
  getCategories,
} from "./components/ApiProducts";
import { checkTokenActive } from "./components/api";
import { MessageAlertTokenExpire } from "./customStyles";
import { getHeaderImage } from "./themeConfig";

const isDevMode = process.env.REACT_APP_DEV_MODE === "true";
const DEV_SEDE_ID = process.env.REACT_APP_SEDE_ID || "00301";

const App = () => {
  const [showCatalog, setShowCatalog] = useState(false);
  const [categories, setCategories] = useState([]);
  const [sedeId, setSedeId] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [sessionData, setSessionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const urlParams = new URLSearchParams(window.location.search);
  const sessionIdFromUrl = urlParams.get("session_id");
  const tokenFromUrl = urlParams.get("token");

  useEffect(() => {
    const initApp = async () => {
      try {
        // ── Dev mode: skip auth, use default sede ──
        if (isDevMode) {
          const devSessionId = tokenFromUrl || sessionIdFromUrl || "dev-session";
          console.log("DEV MODE: Usando session/token:", devSessionId);
          setSessionId(devSessionId);

          // Login to Asertis API (still needed for product fetching)
          await login();

          let devSedeId = DEV_SEDE_ID;

          if (tokenFromUrl) {
            const sessionFromCart = await getCartSessionData(tokenFromUrl);
            if (sessionFromCart) {
              setSessionData(sessionFromCart);
              devSedeId = sessionFromCart?.sede?.sede_id || DEV_SEDE_ID;
              console.log(
                "DEV MODE: Sede resuelta desde carrito:",
                devSedeId,
                "- Cliente:",
                sessionFromCart?.client?.name || "sin nombre"
              );
            }
          }

          console.log("DEV MODE: Usando sede_id:", devSedeId);
          setSedeId(devSedeId);

          const categoriesData = await getCategories(devSedeId);
          setCategories(categoriesData);
          setShowCatalog(true);
          setIsLoading(false);
          return;
        }

        // ── Production mode ──
        if (!sessionIdFromUrl && !tokenFromUrl) {
          MessageAlertTokenExpire();
          setIsLoading(false);
          return;
        }

        setSessionId(tokenFromUrl || sessionIdFromUrl);

        // 1. Login to Asertis API
        await login();

        // 2. Validate shared token explicitly when present.
        if (tokenFromUrl) {
          const isTokenActive = await checkTokenActive(tokenFromUrl);
          if (!isTokenActive) {
            MessageAlertTokenExpire();
            setIsLoading(false);
            return;
          }
        }

        // 3. If a token is present without session_id, load sede/client data from cart.
        if (!sessionIdFromUrl && tokenFromUrl) {
          const sessionFromCart = await getCartSessionData(tokenFromUrl);
          if (!sessionFromCart || !sessionFromCart.sede) {
            Swal.fire({
              icon: "error",
              title: "No se pudo cargar la sesión",
              text: "El token es válido, pero no fue posible obtener la sede o los datos del carrito.",
              confirmButtonColor: "#e63946",
            });
            setIsLoading(false);
            return;
          }

          setSessionData(sessionFromCart);
          const currentSedeId = sessionFromCart.sede.sede_id;
          setSedeId(currentSedeId);
          console.log(
            "Sede ID:",
            currentSedeId,
            "- Sede:",
            sessionFromCart.sede.sede
          );

          const categoriesData = await getCategories(currentSedeId);
          if (!categoriesData || categoriesData.length === 0) {
            Swal.fire({
              icon: "warning",
              title: "Sin categorías disponibles",
              text: "No se encontraron categorías para esta sede.",
              confirmButtonColor: "#e63946",
            });
            setIsLoading(false);
            return;
          }

          setCategories(categoriesData);
          setShowCatalog(true);
          setIsLoading(false);
          return;
        }

        // 4. Get session data (sede_id, client info, etc.)
        const session = await getSessionData(sessionIdFromUrl);
        if (!session || !session.sede) {
          Swal.fire({
            icon: "error",
            title: "No se pudo cargar la sesión",
            text: "La validación del token fue correcta, pero la consulta de datos de sesión no devolvió la sede.",
            confirmButtonColor: "#e63946",
          });
          setIsLoading(false);
          return;
        }

        setSessionData(session);
        const currentSedeId = session.sede.sede_id;
        setSedeId(currentSedeId);
        console.log("Sede ID:", currentSedeId, "- Sede:", session.sede.sede);

        // 5. Load categories for this sede
        const categoriesData = await getCategories(currentSedeId);
        if (!categoriesData || categoriesData.length === 0) {
          Swal.fire({
            icon: "warning",
            title: "Sin categorías disponibles",
            text: "No se encontraron categorías para esta sede.",
            confirmButtonColor: "#e63946",
          });
          setIsLoading(false);
          return;
        }
        setCategories(categoriesData);
        setShowCatalog(true);
        setIsLoading(false);
      } catch (error) {
        console.error("Error al inicializar la aplicación:", error);
        Swal.fire({
          icon: "error",
          title: "Error de conexión",
          text: "No se pudo conectar con el servidor. Intente nuevamente.",
          confirmButtonColor: "#e63946",
        });
        setIsLoading(false);
      }
    };

    initApp();
  }, []);

  return (
    <div className="App">
      <header className="App-header">
        <div className="logo-container">
          <img src={getHeaderImage()} alt="Logo" />
        </div>
      </header>
      <main className="product-container">
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Cargando catálogo...</p>
          </div>
        ) : showCatalog ? (
          <ProductMenu
            categories={categories}
            sedeId={sedeId}
            sessionId={sessionId}
            sessionData={sessionData}
          />
        ) : null}
      </main>
    </div>
  );
};

export default App;
